import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LegalDoc {
  version: string;
  title: string;
  content_md: string;
  effective_at: string;
}

interface Props {
  slug: string;
  fallbackTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept?: () => void;
}

/** Shows a legal document in-page so filled forms are never lost by navigating away. */
export function LegalDocDialog({ slug, fallbackTitle, open, onOpenChange, onAccept }: Props) {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || doc) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from("legal_documents")
        .select("version, title, content_md, effective_at")
        .eq("slug", slug)
        .eq("locale", "pt-BR")
        .is("superseded_at", null)
        .order("effective_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) setError("Não foi possível carregar o documento.");
      else if (!data) setError("Documento indisponível no momento.");
      else setDoc(data as LegalDoc);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, slug, doc]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{doc?.title ?? fallbackTitle}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ) : error ? (
            <p className="text-muted-foreground text-sm">{error}</p>
          ) : doc ? (
            <article className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
              <div className="mb-4 text-xs text-muted-foreground">
                Versão {doc.version} · Em vigor desde{" "}
                {new Date(doc.effective_at).toLocaleDateString("pt-BR")}
              </div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content_md}</ReactMarkdown>
            </article>
          ) : null}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {onAccept && (
            <Button
              disabled={!doc}
              onClick={() => {
                onAccept();
                onOpenChange(false);
              }}
            >
              Li e concordo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
